import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Checkbox } from "@repo/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { Label } from "@repo/ui/label";
import { ScrollArea } from "@repo/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Play,
  Scale,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Input } from "@repo/ui/input";
import { Search, Home, DollarSign, Camera } from "lucide-react";
import {
  ChartTooltip,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "@repo/ui/chart";
import { buildSuburbProfileConsensus, type ConsensusInputRow } from "@repo/suburb-adapters";
import { useToast } from "@/hooks/use-toast";
import {
  fetchSuburbProfileAdapters,
  fetchSuburbProfileSnapshotsWithAdapter,
  fetchSuburbDetail,
  triggerSuburbCollection,
  type BedroomBandRow,
  type DemographicsRow,
  type SuburbProfileAdapter,
  type SuburbProfileSnapshotWithAdapter,
} from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { supabase } from "@/integrations/supabase/client";

function formatPrice(price: number | null): string {
  if (!price) return "-";
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(1)}M`;
  if (price >= 1_000) return `$${(price / 1_000).toFixed(0)}K`;
  return `$${price.toLocaleString()}`;
}

interface SuburbRow {
  id: string;
  suburb_name: string;
  state: string;
  postcode: string;
}

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function formatMonth(value: string): string {
  const [y, m] = value.split("-");
  if (!y || !m) return value;
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-AU", {
    month: "short",
    year: "numeric",
  });
}

interface MetricSparklineProps {
  title: string;
  data: SuburbProfileSnapshotWithAdapter[];
  field: keyof SuburbProfileSnapshotWithAdapter;
  formatter: (value: number | null) => string;
  currentValue: number | null;
}

/** Label/value pair row used inside the Demographics card. */
const FragmentRow = ({ label, value }: { label: string; value: string }) => (
  <>
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium">{value}</span>
  </>
);

const MetricSparkline = ({ title, data, field, formatter, currentValue }: MetricSparklineProps) => {
  const chartData = data
    .filter((s) => s[field] !== null && s[field] !== undefined)
    .map((s) => ({ month: s.snapshot_month, value: Number(s[field]), adapter: s.adapter_slug }))
    .reverse();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-xl">{formatter(currentValue)}</CardTitle>
      </CardHeader>
      <CardContent className="h-20 p-2">
        {chartData.length < 2 ? (
          <p className="text-xs text-muted-foreground">Need at least 2 snapshots to chart.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="month" hide />
              <YAxis hide domain={["auto", "auto"]} />
              <ChartTooltip
                contentStyle={{ fontSize: "0.75rem" }}
                labelFormatter={(m: unknown) => formatMonth(String(m))}
                formatter={(v: unknown) => formatter(Number(v))}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const SuburbDetail = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { suburbId } = useParams<{ suburbId: string }>();
  const [rawDataOpen, setRawDataOpen] = useState<SuburbProfileSnapshotWithAdapter | null>(null);
  const [adapterFilter, setAdapterFilter] = useState<string | "all">("all");
  const [rerunSlugs, setRerunSlugs] = useState<string[]>([]);
  const [propertySearch, setPropertySearch] = useState("");

  const { data: suburb } = useQuery<SuburbRow | null>({
    queryKey: ["suburb", suburbId],
    queryFn: async () => {
      if (!suburbId) return null;
      const { data, error } = await supabase
        .from("suburbs")
        .select("id, suburb_name, state, postcode")
        .eq("id", suburbId)
        .maybeSingle();
      if (error) throw error;
      return data as SuburbRow | null;
    },
    enabled: !!suburbId,
  });

  // Property aggregates + table. Merged from the old SuburbDetail page so
  // a single URL covers "info about this suburb" + "properties we have
  // for this suburb". Disabled until we know the suburb_name so we don't
  // fire a broad scan with empty filter.
  const { data: propertyData } = useQuery({
    queryKey: ["suburb-properties", suburb?.suburb_name],
    queryFn: () =>
      suburb?.suburb_name
        ? fetchSuburbDetail(suburb.suburb_name)
        : Promise.resolve(null),
    enabled: !!suburb?.suburb_name,
  });

  const properties = propertyData?.properties ?? [];
  const propertyStats = propertyData?.stats;
  const filteredProperties = useMemo(() => {
    const q = propertySearch.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) =>
      `${p.full_address} ${p.property_type ?? ""}`.toLowerCase().includes(q),
    );
  }, [properties, propertySearch]);

  const { data: snapshots, isLoading } = useQuery<SuburbProfileSnapshotWithAdapter[]>({
    queryKey: ["suburb-profile-snapshots-adapter", suburbId],
    queryFn: () =>
      suburbId ? fetchSuburbProfileSnapshotsWithAdapter(suburbId, 60) : Promise.resolve([]),
    enabled: !!suburbId,
  });

  const { data: adapters } = useQuery<SuburbProfileAdapter[]>({
    queryKey: ["suburb-profile-adapters"],
    queryFn: fetchSuburbProfileAdapters,
  });

  // Distinct adapter slugs that have produced at least one snapshot for
  // this suburb — used for the per-adapter filter chips. Falls back to
  // the global adapter list so the user can still trigger an adapter
  // that hasn't run for this suburb yet.
  const adapterSlugsWithData = useMemo(() => {
    const set = new Set<string>();
    for (const s of snapshots ?? []) if (s.adapter_slug) set.add(s.adapter_slug);
    return Array.from(set);
  }, [snapshots]);

  const filteredSnapshots = useMemo(() => {
    if (!snapshots) return [];
    if (adapterFilter === "all") return snapshots;
    return snapshots.filter((s) => s.adapter_slug === adapterFilter);
  }, [snapshots, adapterFilter]);

  const latestByAdapter = useMemo(() => {
    const map = new Map<string, SuburbProfileSnapshotWithAdapter>();
    for (const s of snapshots ?? []) {
      const key = s.adapter_slug ?? "_unknown";
      if (!map.has(key)) map.set(key, s);
    }
    return map;
  }, [snapshots]);

  /**
   * Consensus across whichever adapters have a snapshot for the most-
   * recent month any adapter reported. Per the plan: per-adapter rows
   * stay untouched in the DB; this is a display-time blend that the
   * UI can highlight when adapters meaningfully disagree.
   */
  const consensus = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return null;
    // Group by month so we can pick the most-recent month that has
    // ≥1 contribution. Even if only one adapter reported, we still
    // surface the card — the disagreement badge just stays inactive.
    const byMonth = new Map<string, SuburbProfileSnapshotWithAdapter[]>();
    for (const s of snapshots) {
      const bucket = byMonth.get(s.snapshot_month) ?? [];
      bucket.push(s);
      byMonth.set(s.snapshot_month, bucket);
    }
    const sortedMonths = Array.from(byMonth.keys()).sort().reverse();
    const target = sortedMonths.find((m) => (byMonth.get(m)?.length ?? 0) >= 1);
    if (!target) return null;
    const rows = byMonth.get(target) ?? [];
    const input: ConsensusInputRow[] = rows.map((s) => ({
      adapterSlug: s.adapter_slug ?? "unknown",
      adapterName: s.adapter_name,
      snapshotMonth: s.snapshot_month,
      medianHousePrice: s.median_house_price,
      medianUnitPrice: s.median_unit_price,
      annualGrowthPct: s.annual_growth_pct,
      fiveYearGrowthPct: s.five_year_growth_pct,
      houseSalesVolume12mo: s.house_sales_volume_12mo,
      unitSalesVolume12mo: s.unit_sales_volume_12mo,
      rentalYieldHousePct: s.rental_yield_house_pct,
      daysOnMarketHouse: s.days_on_market_house,
    }));
    return { month: target, result: buildSuburbProfileConsensus(input), contributors: rows };
  }, [snapshots]);

  /**
   * Pick the highest-volume bedroom band from a snapshot's
   * bedroom_breakdowns to use as the "headline" for the growth chart.
   * Falls back to the first band when sales volume is missing.
   */
  const growthHistorySeries = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    const series: Array<{
      adapterSlug: string;
      bedrooms: number | null;
      points: Array<{ year: number; growth: number | null; sold: number | null }>;
    }> = [];
    for (const [slug, snap] of latestByAdapter) {
      const bands = snap.bedroom_breakdowns ?? [];
      if (bands.length === 0) continue;
      const best = [...bands]
        .filter((b) => Array.isArray(b.growthHistory) && (b.growthHistory?.length ?? 0) >= 2)
        .sort((a, b) => (b.salesVolume12mo ?? 0) - (a.salesVolume12mo ?? 0))[0];
      if (!best?.growthHistory) continue;
      type HistoryPoint = NonNullable<BedroomBandRow["growthHistory"]>[number];
      series.push({
        adapterSlug: slug,
        bedrooms: best.bedrooms ?? null,
        points: best.growthHistory
          .filter(
            (p: HistoryPoint) =>
              !!p.year && p.annualGrowth !== null && p.annualGrowth !== 0,
          )
          .sort((a: HistoryPoint, b: HistoryPoint) => a.year - b.year)
          .map((p: HistoryPoint) => ({
            year: p.year,
            growth: p.annualGrowth ?? null,
            sold: p.numberSold ?? null,
          })),
      });
    }
    return series;
  }, [snapshots, latestByAdapter]);

  const latest = useMemo(() => filteredSnapshots[0], [filteredSnapshots]);

  const toggleRerun = (slug: string) => {
    setRerunSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const rerunMutation = useMutation({
    mutationFn: async () => {
      if (!suburbId) throw new Error("No suburb selected.");
      if (rerunSlugs.length === 0) throw new Error("Pick at least one adapter.");
      return triggerSuburbCollection({
        suburbIds: [suburbId],
        adapterSlugs: rerunSlugs,
        runType: "manual",
        force: true,
      });
    },
    onSuccess: (result) => {
      const summary = (result.runs ?? [])
        .map((r) => `${r.adapterSlug}: ${r.done}/${r.suburbsTotal}`)
        .join(" · ");
      toast({
        title: "Collection complete",
        description: summary || `${result.done}/${result.suburbsTotal}`,
      });
      queryClient.invalidateQueries({
        queryKey: ["suburb-profile-snapshots-adapter", suburbId],
      });
      queryClient.invalidateQueries({ queryKey: ["latest-suburb-snapshots"] });
    },
    onError: (err) => {
      toast({
        title: "Collection failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/dashboard/suburbs"
          className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> All suburbs
        </Link>
        {suburb ? (
          <div>
            <h1 className="text-2xl font-bold">
              {suburb.suburb_name}, {suburb.state} {suburb.postcode}
            </h1>
            {latest && (
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                <span>Latest snapshot:</span>
                <Badge variant="outline">{formatMonth(latest.snapshot_month)}</Badge>
                {latest.adapter_slug ? (
                  <Badge variant="secondary" className="font-mono text-xs">
                    {latest.adapter_slug}
                  </Badge>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          <h1 className="text-2xl font-bold">Suburb</h1>
        )}
      </div>

      {/* Property aggregates from the properties table — counts, sold
          volumes by recency window, sources. Surfaced here so the
          unified suburb page covers both the suburb-profile rollup
          (below) and the per-property data we've collected. */}
      {propertyStats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PropertyStatCard
            icon={<Home className="w-4 h-4 text-muted-foreground" />}
            label="Properties"
            value={propertyStats.propertyCount.toLocaleString()}
          />
          <PropertyStatCard
            icon={<DollarSign className="w-4 h-4 text-green-500" />}
            label="Median sale price"
            value={formatPrice(propertyStats.medianSalePrice)}
          />
          <PropertyStatCard
            icon={<Camera className="w-4 h-4 text-muted-foreground" />}
            label="Total snapshots"
            value={propertyStats.totalSnapshots.toLocaleString()}
            sub={`avg ${propertyStats.avgSnapshotsPerProperty.toFixed(1)} / property`}
          />
          <PropertyStatCard
            label="Sold (30/60/90d)"
            value={`${propertyStats.sold30} / ${propertyStats.sold60} / ${propertyStats.sold90}`}
          />
        </div>
      ) : null}

      {/* Multi-adapter consensus card — sales-volume-weighted blend
          when ≥1 adapter reported the most recent month. Visible badge
          when adapters meaningfully disagree (>5%). Per-adapter rows
          stay untouched in the DB; this is a display-time view only. */}
      {consensus ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="h-4 w-4" />
              Consensus · {formatMonth(consensus.month)}
            </CardTitle>
            <CardDescription>
              Sales-volume-weighted aggregate across {consensus.contributors.length}{" "}
              adapter
              {consensus.contributors.length === 1 ? "" : "s"} for this month.
              Disagreements &gt; 5% surface as deltas next to each adapter's value.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(["medianHousePrice", "medianUnitPrice", "annualGrowthPct", "daysOnMarketHouse"] as const)
                .filter((field) => consensus.result[field].value !== null)
                .map(
                (field) => {
                  const f = consensus.result[field];
                  const label =
                    field === "medianHousePrice"
                      ? "Median house"
                      : field === "medianUnitPrice"
                        ? "Median unit"
                        : field === "annualGrowthPct"
                          ? "Annual growth"
                          : "Days on market";
                  const fmt = (v: number | null) =>
                    field === "medianHousePrice" || field === "medianUnitPrice"
                      ? formatCurrency(v)
                      : field === "annualGrowthPct"
                        ? formatPercent(v)
                        : v === null
                          ? "—"
                          : `${Math.round(v)} days`;
                  return (
                    <div key={field} className="rounded-md border p-3">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="text-xl font-semibold mt-1">{fmt(f.value)}</p>
                      <div className="mt-2 space-y-1">
                        {f.perAdapter.map((p) => (
                          <div key={p.adapterSlug} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-muted-foreground">{p.adapterSlug}</span>
                            <span className="flex items-center gap-2">
                              <span>{fmt(p.value)}</span>
                              {p.deltaPct !== null && Math.abs(p.deltaPct) > 0.05 ? (
                                <Badge variant="destructive" className="text-[10px] py-0 px-1">
                                  Δ {p.deltaPct >= 0 ? "+" : ""}
                                  {(p.deltaPct * 100).toFixed(1)}%
                                </Badge>
                              ) : null}
                            </span>
                          </div>
                        ))}
                      </div>
                      {f.significantDisagreement ? (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-destructive">
                          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                          <span>
                            Adapters disagree by {Math.round((f.maxDeviationPct ?? 0) * 100)}%
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                },
              )}
            </div>
            {consensus.result.combinedHouseSalesVolume12mo ? (
              <p className="text-xs text-muted-foreground">
                Combined house sales (12mo, summed across adapters):{" "}
                <span className="font-medium text-foreground">
                  {consensus.result.combinedHouseSalesVolume12mo}
                </span>{" "}
                — likely double-counts cross-listings; treat as a ceiling.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Per-adapter latest summary so the user sees what each adapter
          reported for this suburb side-by-side. */}
      {snapshots && snapshots.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Per-adapter latest</CardTitle>
            <CardDescription>
              Most recent snapshot per adapter — handy when Domain and
              realestate.com.au disagree.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {Array.from(latestByAdapter.entries()).map(([slug, snap]) => (
              <div key={slug} className="rounded-md border p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {slug}
                  </Badge>
                  <Badge variant="outline">{formatMonth(snap.snapshot_month)}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{snap.adapter_name}</p>
                <div className="grid grid-cols-2 gap-1 text-sm">
                  <div className="text-muted-foreground">Median house</div>
                  <div className="text-right font-medium">
                    {formatCurrency(snap.median_house_price)}
                  </div>
                  {snap.median_unit_price !== null && snap.median_unit_price !== undefined ? (
                    <>
                      <div className="text-muted-foreground">Median unit</div>
                      <div className="text-right font-medium">
                        {formatCurrency(snap.median_unit_price)}
                      </div>
                    </>
                  ) : null}
                  <div className="text-muted-foreground">Annual growth</div>
                  <div
                    className={`text-right font-medium ${
                      snap.annual_growth_pct === null
                        ? ""
                        : snap.annual_growth_pct >= 0
                          ? "text-success"
                          : "text-destructive"
                    }`}
                  >
                    {formatPercent(snap.annual_growth_pct)}
                  </div>
                  <div className="text-muted-foreground">House sales (12mo)</div>
                  <div className="text-right font-medium">
                    {snap.house_sales_volume_12mo ?? "—"}
                  </div>
                  {snap.unit_sales_volume_12mo !== null && snap.unit_sales_volume_12mo !== undefined ? (
                    <>
                      <div className="text-muted-foreground">Unit sales (12mo)</div>
                      <div className="text-right font-medium">
                        {snap.unit_sales_volume_12mo}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Per-bedroom-band breakdown — when an adapter exposed
          propertyCategories[] / market-insights byBedroom[] this surfaces
          every band rather than the collapsed hero number. */}
      {Array.from(latestByAdapter.entries()).some(
        ([, s]) => (s.bedroom_breakdowns?.length ?? 0) > 0,
      ) ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Breakdown by bedroom band</CardTitle>
            <CardDescription>
              One section per adapter that exposed per-bedroom stats. Bands with no
              data are omitted.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from(latestByAdapter.entries()).map(([slug, snap]) => {
              const bands = (snap.bedroom_breakdowns ?? []) as BedroomBandRow[];
              if (bands.length === 0) return null;
              // Group by dwellingType first (houses, then units), then sort
              // by bedrooms within each group — keeps like-for-like rows
              // adjacent so houses don't visually mix with units.
              const dwellingRank = (t: string | null | undefined) =>
                t === "house" ? 0 : t === "unit" ? 1 : 2;
              const sorted = [...bands].sort((a, b) => {
                const dr = dwellingRank(a.dwellingType) - dwellingRank(b.dwellingType);
                if (dr !== 0) return dr;
                return (a.bedrooms ?? 99) - (b.bedrooms ?? 99);
              });
              return (
                <div key={slug} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="font-mono text-xs">
                      {slug}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatMonth(snap.snapshot_month)} · {bands.length} band
                      {bands.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bedrooms</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Median sold</TableHead>
                        <TableHead>Median rent</TableHead>
                        <TableHead>Days on mkt</TableHead>
                        <TableHead>Sales 12mo</TableHead>
                        <TableHead>Annual growth</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sorted.map((b, idx) => (
                        <TableRow key={`${slug}-${b.bedrooms ?? "all"}-${idx}`}>
                          <TableCell className="font-medium">
                            {b.bedrooms ?? "all"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {b.dwellingType ?? "—"}
                          </TableCell>
                          <TableCell>{formatCurrency(b.medianSoldPrice ?? null)}</TableCell>
                          <TableCell>
                            {b.medianRentWeekly == null
                              ? "—"
                              : `$${Math.round(b.medianRentWeekly).toLocaleString("en-AU")}/wk`}
                          </TableCell>
                          <TableCell>
                            {b.daysOnMarket == null ? "—" : `${b.daysOnMarket} days`}
                          </TableCell>
                          <TableCell>{b.salesVolume12mo ?? "—"}</TableCell>
                          <TableCell
                            className={
                              b.annualGrowthPct == null
                                ? ""
                                : b.annualGrowthPct >= 0
                                  ? "text-success"
                                  : "text-destructive"
                            }
                          >
                            {formatPercent(b.annualGrowthPct ?? null)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}

      {/* Multi-year growth-history chart — drawn from the highest-volume
          bedroom band of each adapter so the line stays meaningful. */}
      {growthHistorySeries.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Multi-year growth history</CardTitle>
            <CardDescription>
              Year-by-year annual growth from each adapter's highest-volume
              bedroom band. Years with zero or missing growth are dropped.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {growthHistorySeries.map((series) => (
              <div key={series.adapterSlug} className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {series.adapterSlug}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {series.bedrooms ?? "?"}-bed band · {series.points.length}{" "}
                    year
                    {series.points.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series.points}>
                      <XAxis dataKey="year" />
                      <YAxis hide domain={["auto", "auto"]} />
                      <ChartTooltip
                        contentStyle={{ fontSize: "0.75rem" }}
                        formatter={(v: unknown) => formatPercent(Number(v))}
                      />
                      <Line
                        type="monotone"
                        dataKey="growth"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Demographics — sparse per-adapter card. Each adapter may
          contribute different keys; we render whatever is present. */}
      {Array.from(latestByAdapter.entries()).some(
        ([, s]) => s.demographics && Object.keys(s.demographics ?? {}).length > 0,
      ) ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" /> Demographics
            </CardTitle>
            <CardDescription>
              Population + age + ownership stats sourced from each adapter's
              latest snapshot. Sparse — not every adapter exposes every key.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {Array.from(latestByAdapter.entries()).map(([slug, snap]) => {
              const d = (snap.demographics ?? {}) as DemographicsRow;
              if (Object.keys(d).length === 0) return null;
              const entries: Array<[string, string]> = [];
              if (d.population != null)
                entries.push(["Population", d.population.toLocaleString("en-AU")]);
              if (d.medianAge != null) entries.push(["Median age", String(d.medianAge)]);
              if (d.medianHouseholdIncome != null)
                entries.push([
                  "Median household income",
                  formatCurrency(d.medianHouseholdIncome),
                ]);
              if (d.ownerOccupierPct != null)
                entries.push([
                  "Owner-occupier %",
                  formatPercent(d.ownerOccupierPct),
                ]);
              if (d.renterPct != null)
                entries.push(["Renter %", formatPercent(d.renterPct)]);
              if (d.marriedPct != null)
                entries.push(["Married %", formatPercent(d.marriedPct)]);
              if (d.singlePct != null)
                entries.push(["Single %", formatPercent(d.singlePct)]);
              if (d.mostCommonAgeBracket)
                entries.push(["Most common age", d.mostCommonAgeBracket]);
              return (
                <div key={slug} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="font-mono text-xs">
                      {slug}
                    </Badge>
                    <Badge variant="outline">{formatMonth(snap.snapshot_month)}</Badge>
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
                    {entries.map(([k, v]) => (
                      <FragmentRow key={k} label={k} value={v} />
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}

      {/* Re-run a fresh snapshot for the current month. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run adapters for current month</CardTitle>
          <CardDescription>
            Re-runs are idempotent within a month (existing snapshot is
            preserved) unless force is implied by manual trigger. We can only
            crawl the current month — history builds over time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            {(adapters ?? []).map((a) => (
              <div key={a.id} className="flex items-center gap-2">
                <Checkbox
                  id={`detail-adapter-${a.slug}`}
                  checked={rerunSlugs.includes(a.slug)}
                  onCheckedChange={() => toggleRerun(a.slug)}
                />
                <Label
                  htmlFor={`detail-adapter-${a.slug}`}
                  className="font-normal cursor-pointer"
                >
                  {a.name}{" "}
                  <span className="text-muted-foreground text-xs">({a.slug})</span>
                </Label>
              </div>
            ))}
          </div>
          <Button
            type="button"
            onClick={() => rerunMutation.mutate()}
            disabled={rerunMutation.isPending || rerunSlugs.length === 0}
          >
            {rerunMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Run {rerunSlugs.length || 0} adapter(s)
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading snapshots…
        </div>
      ) : !snapshots || snapshots.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              No suburb-profile snapshots for this suburb yet. Use the runner
              above to fetch the current month.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricSparkline
              title="Median house price"
              data={filteredSnapshots}
              field="median_house_price"
              formatter={formatCurrency}
              currentValue={latest?.median_house_price ?? null}
            />
            <MetricSparkline
              title="Median unit price"
              data={filteredSnapshots}
              field="median_unit_price"
              formatter={formatCurrency}
              currentValue={latest?.median_unit_price ?? null}
            />
            <MetricSparkline
              title="Annual growth"
              data={filteredSnapshots}
              field="annual_growth_pct"
              formatter={formatPercent}
              currentValue={latest?.annual_growth_pct ?? null}
            />
            <MetricSparkline
              title="Rental yield (house)"
              data={filteredSnapshots}
              field="rental_yield_house_pct"
              formatter={formatPercent}
              currentValue={latest?.rental_yield_house_pct ?? null}
            />
            <MetricSparkline
              title="Median house rent / week"
              data={filteredSnapshots}
              field="median_house_rent_weekly"
              formatter={formatCurrency}
              currentValue={latest?.median_house_rent_weekly ?? null}
            />
            <MetricSparkline
              title="Days on market (house)"
              data={filteredSnapshots}
              field="days_on_market_house"
              formatter={(v) => (v === null ? "—" : `${v} days`)}
              currentValue={latest?.days_on_market_house ?? null}
            />
            <MetricSparkline
              title="Population"
              data={filteredSnapshots}
              field="population"
              formatter={(v) =>
                v === null ? "—" : new Intl.NumberFormat("en-AU").format(v)
              }
              currentValue={latest?.population ?? null}
            />
            <MetricSparkline
              title="Median household income"
              data={filteredSnapshots}
              field="median_household_income"
              formatter={formatCurrency}
              currentValue={latest?.median_household_income ?? null}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Snapshot history</CardTitle>
              <CardDescription>
                Every captured snapshot for this suburb, with the adapter that
                produced it. Filter by adapter to compare timelines.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Adapter filter chips — "all" plus one per slug we have. */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setAdapterFilter("all")}
                  className={`text-xs px-2 py-1 rounded border ${
                    adapterFilter === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background"
                  }`}
                >
                  all ({snapshots.length})
                </button>
                {adapterSlugsWithData.map((slug) => {
                  const count = snapshots.filter((s) => s.adapter_slug === slug).length;
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => setAdapterFilter(slug)}
                      className={`text-xs px-2 py-1 rounded border font-mono ${
                        adapterFilter === slug
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background"
                      }`}
                    >
                      {slug} ({count})
                    </button>
                  );
                })}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Adapter</TableHead>
                    <TableHead>Median house</TableHead>
                    <TableHead>Median unit</TableHead>
                    <TableHead>Growth</TableHead>
                    <TableHead>Yield (H)</TableHead>
                    <TableHead>Sales (H/U)</TableHead>
                    <TableHead className="text-right">Raw</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSnapshots.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{formatMonth(s.snapshot_month)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono text-xs">
                          {s.adapter_slug ?? "?"}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(s.median_house_price)}</TableCell>
                      <TableCell>{formatCurrency(s.median_unit_price)}</TableCell>
                      <TableCell>{formatPercent(s.annual_growth_pct)}</TableCell>
                      <TableCell>{formatPercent(s.rental_yield_house_pct)}</TableCell>
                      <TableCell className="text-xs">
                        {s.house_sales_volume_12mo ?? "—"} /{" "}
                        {s.unit_sales_volume_12mo ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setRawDataOpen(s)}
                        >
                          View raw
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!rawDataOpen} onOpenChange={(open) => !open && setRawDataOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {rawDataOpen &&
                `Snapshot · ${formatMonth(rawDataOpen.snapshot_month)} · ${
                  rawDataOpen.adapter_slug ?? "?"
                }`}
            </DialogTitle>
          </DialogHeader>
          {rawDataOpen && (
            <ScrollArea className="h-[60vh] rounded border">
              <pre className="text-xs p-4 whitespace-pre-wrap break-all">
                {JSON.stringify(rawDataOpen.raw_data, null, 2)}
              </pre>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Properties in this suburb — deduplicated rows from `properties`,
          merged in from the old SuburbDetail page so the unified suburb
          page is a one-stop shop. Click a row to drill into the property
          detail page. */}
      {propertyData ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Properties</CardTitle>
            <CardDescription>
              {filteredProperties.length === properties.length
                ? `${properties.length} deduplicated properties`
                : `${filteredProperties.length} of ${properties.length} match`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="suburb-detail-property-search"
                name="suburbDetailPropertySearch"
                placeholder="Filter by address or type…"
                value={propertySearch}
                onChange={(e) => setPropertySearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead className="w-[80px] text-center">Beds</TableHead>
                  <TableHead className="w-[140px] text-right">Last sale</TableHead>
                  <TableHead className="w-[120px] text-right">Sale date</TableHead>
                  <TableHead className="w-[80px] text-center">Snaps</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProperties.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      <Home className="w-6 h-6 mx-auto opacity-50 mb-2" />
                      No properties match this filter
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProperties.map((property) => (
                    <TableRow
                      key={property.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/dashboard/properties/${property.id}`)}
                    >
                      <TableCell className="font-medium text-sm">
                        {property.full_address}
                      </TableCell>
                      <TableCell>
                        {property.property_type ? (
                          <Badge variant="outline" className="text-xs font-normal">
                            {property.property_type}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {property.bedrooms ?? "-"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatPrice(property.last_sale_price ?? property.price)}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {formatDate(property.last_sale_date)}
                      </TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {property.snapshot_count > 0 ? property.snapshot_count : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

interface PropertyStatCardProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}

function PropertyStatCard({ icon, label, value, sub }: PropertyStatCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default SuburbDetail;
